import React from 'react';
import { Lock, Menu } from 'lucide-react';
import { JourneyStep, StepBadge } from './JourneyRail';

/**
 * Collapsed journey rail — `JourneyRail` reduced to its step markers. It holds
 * its place in the wizard layout at the slim width; labels, sub-steps and
 * Robbie's tip come back either as the hover/focus flyout or, once pinned, as
 * the full in-flow rail.
 *
 * The badges are the same component the full rail draws, so a number stays a
 * number and a finished step stays a tick at either width — the one thing
 * somebody glancing at a 60px column actually needs to read. Every marker
 * carries its label as `aria-label` + `title`, because at this width the badge
 * is all there is.
 *
 * Purely presentational: the caller owns pin state and hover intent
 * (`useSidebarRail`).
 */
export interface JourneyRailSlimProps {
  steps: JourneyStep[];
  /** Locked future stages, shown as their own markers exactly as the full rail. */
  locked?: string[];
  onStepSelect?: (id: string) => void;
  /** Journey is docked in flow; drives the toggle's pressed state. */
  pinned: boolean;
  onTogglePin: () => void;
  /** Focus target when Escape closes the flyout. */
  pinButtonRef?: React.RefObject<HTMLButtonElement | null>;
  className?: string;
}

const SlimStep: React.FC<{
  step: JourneyStep;
  index: number;
  onSelect?: (id: string) => void;
}> = ({ step, index, onSelect }) => {
  const active = step.state === 'active';
  return (
    <div className="flex flex-col items-center">
      <button
        type="button"
        onClick={() => onSelect?.(step.id)}
        aria-current={active ? 'step' : undefined}
        aria-label={step.label}
        title={step.state === 'upcoming' || step.state === 'visited' ? step.reason ?? step.label : step.label}
        className={`relative flex h-[34px] w-[38px] items-center justify-center rounded-control ${
          active ? 'bg-action-bg' : 'hover:bg-canvas'
        } ${step.state === 'upcoming' ? 'cursor-default opacity-70' : ''}`}
      >
        {active && <span className="absolute bottom-[5px] left-[-7px] top-[5px] w-[2.5px] rounded-[2px] bg-action" />}
        <StepBadge index={index} state={step.state} />
      </button>
      {/* The active step's sub-steps survive the collapse as dots: three
          positions is the whole of step 3's shape, and losing it would make
          the rail lie about where the user is. */}
      {active && step.subSteps && step.subSteps.length > 0 && (
        <div className="flex items-center gap-[3px] pb-[2px] pt-[3px]">
          {step.subSteps.map((sub) => (
            <span
              key={sub.id}
              title={sub.label}
              className={`h-[5px] w-[5px] rounded-round ${
                sub.state === 'done' ? 'bg-success-ink' : sub.state === 'active' ? 'bg-action' : 'bg-line-strong'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const JourneyRailSlim: React.FC<JourneyRailSlimProps> = ({
  steps,
  locked = [],
  onStepSelect,
  pinned,
  onTogglePin,
  pinButtonRef,
  className = '',
}) => (
  <aside
    className={`flex h-full flex-col items-center overflow-y-auto border-r border-line bg-surface py-s6 ${className}`}
  >
    <button
      ref={pinButtonRef}
      type="button"
      onClick={onTogglePin}
      aria-label={pinned ? 'Collapse the RFQ journey' : 'Expand and keep the RFQ journey open'}
      aria-expanded={pinned}
      aria-pressed={pinned}
      title={pinned ? 'Collapse the RFQ journey' : 'Expand and keep the RFQ journey open'}
      className="mb-s4 flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-control text-ink-muted hover:bg-canvas hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-action"
    >
      <Menu className="h-[16px] w-[16px]" />
    </button>
    <div className="flex flex-col items-center gap-[2px]">
      {steps.map((step, i) => (
        <SlimStep key={step.id} step={step} index={i + 1} onSelect={onStepSelect} />
      ))}
      {locked.length > 0 && (
        <>
          <div className="my-s3 h-px w-[24px] bg-neutral-border" />
          {locked.map((label) => (
            <div
              key={label}
              className="flex h-[30px] w-[38px] items-center justify-center opacity-55"
              aria-disabled="true"
              title={`${label} — coming soon`}
            >
              <span className="flex h-[22px] w-[22px] items-center justify-center rounded-round bg-canvas text-[#98A2B3]">
                <Lock className="h-[10px] w-[10px]" />
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  </aside>
);

export default JourneyRailSlim;
